import React, {useState, useEffect, useRef} from 'react';
import {Modal, Dimmer, Loader, Segment} from 'semantic-ui-react';
import Dropzone from 'react-dropzone';
import {uploadMassUploadFile, downloadMassUploadTemplate} from '../services/massUploadApi';
import {toast} from 'react-toastify';
import SvgNew from '../components/SvgNew';
import './massUploadModal.css';

const MassUploadModal = ({onClose, onUploadComplete}) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const clearData = () => {
        if (!mountedRef.current) return;
        setError(null);
    };

    const handleClose = () => {
        clearData();
        onClose();
    };

    const handleFileSelect = (files) => {
        if (files && files.length > 0) {
            const file = files[0];
            // Проверяем размер файла (75 МБ)
            if (file.size > 78643200) {
                setError({
                    title: 'Размер файла превышает 75 Мб',
                    message: 'Выберите другой файл'
                });
                return;
            }
            // Проверяем формат файла
            if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
                setError({
                    title: 'Неподдерживаемый формат файла',
                    message: 'Пожалуйста, выберите файл Excel (.xlsx или .xls)'
                });
                return;
            }
            setSelectedFile(file);
            setError(null);
        }
    };

    const handleDownloadTemplate = () => {
        downloadMassUploadTemplate()
            .then(() => {
                toast.success('Шаблон успешно скачан');
            })
            .catch(err => {
                console.error(err);
                toast.error('Ошибка при скачивании шаблона');
            });
    };

    const handleUpload = () => {
        if (!selectedFile) {
            toast.warning('Пожалуйста, выберите файл');
            return;
        }

        setUploading(true);
        setError(null);

        uploadMassUploadFile(selectedFile)
            .then(result => {
                if (!mountedRef.current) return;
                
                setUploading(false);
                toast.success(result.message || 'Файл загружен и обрабатывается');
                toast.info('Обработка файла может занять некоторое время. Вы можете продолжить работу.');

                // Переходим на страницу со списком файлов
                onUploadComplete(result);
                handleClose();
            })
            .catch(err => {
                if (!mountedRef.current) return;
                
                setUploading(false);
                console.error('Upload error:', err);
                
                // Обработка различных форматов ошибок
                let errorMessage = 'Повторите попытку или выберете другой файл.';
                
                if (err && typeof err === 'object') {
                    // Если ошибка содержит поле Error (из ErrorResult)
                    if (err.Error) {
                        errorMessage = err.Error;
                    }
                    // Если ошибка содержит поле error (в нижнем регистре)
                    else if (err.error) {
                        errorMessage = err.error;
                    }
                    // Если ошибка содержит поле message
                    else if (err.message) {
                        errorMessage = err.message;
                    }
                    // Если это строка
                    else if (typeof err === 'string') {
                        errorMessage = err;
                    }
                } else if (typeof err === 'string') {
                    errorMessage = err;
                }
                
                setError({
                    title: 'Ошибка загрузки файла',
                    message: errorMessage
                });
            });
    };

    const renderDropzoneBody = () => {
        if (selectedFile) {
            // Показываем превью выбранного файла
            return (
                <div>
                    <Dropzone
                        className='dropzone'
                        multiple={false}
                        onDrop={files => handleFileSelect(files)}
                    >
                        <div className="dropzone-label">
                            <img src="/img/file_icon.png" className="dropzone-icon" alt="file" />
                            <span>{selectedFile.name}</span>
                        </div>
                    </Dropzone>
                    <div className='max-size-text'>Расширения файлов: <span className='color_black'>XLSX, XLS</span></div>
                    <div className='max-size-text'>Максимальный размер файла: <span className='color_black'>75 мб</span></div>
                </div>
            );
        }
        
        return (
            <div>
                <Dropzone
                    className='dropzone'
                    multiple={false}
                    onDrop={files => handleFileSelect(files)}
                >
                    <div className="dropzone-description">
                        <svg width="45" height="40" viewBox="0 0 45 40" fill="none"
                             xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd"
                                  d="M22.2221 0C27.9576 0 33.0109 3.69109 34.8331 8.98884C40.2531 9.72439 44.4442 14.3821 44.4442 19.9999C44.4442 22.7132 43.4575 25.3243 41.6664 27.3532C41.2264 27.8487 40.6153 28.1043 39.9998 28.1043C39.4776 28.1043 38.9531 27.9221 38.5287 27.5487C37.6109 26.7332 37.522 25.331 38.3353 24.4088C39.4087 23.1954 39.9998 21.6265 39.9998 19.9999C39.9998 16.3244 37.0087 13.3333 33.3332 13.3333H33.1109C32.0532 13.3333 31.1421 12.5866 30.9332 11.5488C30.1021 7.43329 26.4399 4.44442 22.2221 4.44442C18.0066 4.44442 14.3421 7.43329 13.5133 11.5488C13.3044 12.5866 12.391 13.3333 11.3333 13.3333H11.1111C7.43551 13.3333 4.44442 16.3244 4.44442 19.9999C4.44442 21.6265 5.03553 23.1954 6.11108 24.4088C6.92218 25.331 6.83552 26.7332 5.91552 27.5487C4.99553 28.3621 3.59109 28.271 2.77998 27.3532C0.986661 25.3243 0 22.7132 0 19.9999C0 14.3821 4.19109 9.72439 9.61106 8.98884C11.4355 3.69109 16.4888 0 22.2221 0ZM20.6779 18.4002C21.5535 17.5646 22.9379 17.5713 23.7935 18.4291L30.4601 25.0957C31.329 25.9646 31.329 27.369 30.4601 28.2379C30.0268 28.6712 29.4579 28.889 28.889 28.889C28.3201 28.889 27.7512 28.6712 27.3179 28.2379L24.4446 25.3646V37.7778C24.4446 39.0067 23.449 40 22.2224 40C20.9957 40 20.0002 39.0067 20.0002 37.7778V25.2357L17.1002 28.0357C16.218 28.8912 14.8113 28.8623 13.958 27.9801C13.1047 27.0957 13.1291 25.6912 14.0113 24.8379L20.6779 18.4002Z"
                                  fill="#135CA9"/>
                        </svg>
                        <div>
                            Перетащите файл в окно или <a>выберите</a>
                        </div>
                    </div>
                </Dropzone>
                <div className='max-size-text'>Расширения файлов: <span className='color_black'>XLSX, XLS</span></div>
                <div className='max-size-text'>Максимальный размер файла: <span className='color_black'>75 мб</span></div>
            </div>
        );
    };

    return (
        <Modal
            open={true}
            closeIcon
            size="small"
            onClose={handleClose}
            className='file-modal'
        >
            <Modal.Header>
                Массовая загрузка накладных
            </Modal.Header>
            <Modal.Content className='modal__content'>
                <Segment vertical>
                    <Dimmer active={uploading} inverted>
                        <Loader size='medium'/>
                    </Dimmer>
                    {renderDropzoneBody()}
                    <a className='modal__template copy-string clickable'
                       onClick={handleDownloadTemplate}>
                        <SvgNew name={'excel'}/> <span>Шаблон файла загрузки с примером заполнения</span>
                    </a>
                    {error && <div className='modal-errors scroll-style'>
                        <div className='modal-error'>
                            <div className='modal-error__label'>{error.title}</div>
                            <div className='modal-error__description'>{error.message}</div>
                        </div>
                    </div>}
                </Segment>
            </Modal.Content>
            <Modal.Actions>
                <button 
                    className="ui primary button"
                    onClick={handleUpload}
                    disabled={!selectedFile || uploading}
                >
                    {uploading ? 'Загрузка...' : 'Загрузить'}
                </button>
                <button 
                    className="ui button"
                    onClick={handleClose}
                >
                    Отмена
                </button>
            </Modal.Actions>
        </Modal>
    );
};

export default MassUploadModal;




// WEBPACK FOOTER //
// ./App/modals/massUploadModal.jsx